import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Modal, IconButton, Typography, Snackbar, Alert } from "@mui/material";
import { Close, Download } from "@mui/icons-material";

import Papa from "papaparse";
import AddCSV from "./AddCSV";
import UserList from "./UserList";
import { uploadUsersByAdmin } from "../../../../store/api/users";
import { getAllGroups } from "../../../../store/api/groups";
import { getTemplateCreationUser } from "../../../../store/api/users";

const UploadUsersModal = ({ open, onClose }) => {
  const queryClient = useQueryClient();

  const [csvData, setCsvData] = useState([]);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(1);
  const [editingRow, setEditingRow] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableUserTypes, setAvailableUserTypes] = useState([]);
  const [uploadResults, setUploadResults] = useState(null);

  // Add success/error toast states
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });

  // Query to fetch all groups
  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ["allGroups"],
    queryFn: () =>
      getAllGroups({
        pageNumber: 1,
        sortBy: "name",
        isDescending: false,
        searchTerm: "",
      }),
  });

  // Query to fetch user types
  const { data: userTypesData, isLoading: userTypesLoading } = useQuery({
    queryKey: ["UserLocationAndType"],
    queryFn: () => getTemplateCreationUser(),
  });

  useEffect(() => {
    if (!groupsLoading && groupsData && groupsData.paginatedContent) {
      setAvailableGroups(groupsData.paginatedContent);
    }
  }, [groupsLoading, groupsData]);

  useEffect(() => {
    if (!userTypesLoading && userTypesData && userTypesData.userTypes) {
      setAvailableUserTypes(userTypesData.userTypes);
    }
  }, [userTypesLoading, userTypesData]);

  // Helper function to find ID by text name
  const findUserTypeId = (typeName) => {
    console.log("Finding user type for:", typeName);
    console.log("Available user types:", availableUserTypes);

    if (!typeName || !availableUserTypes.length) return null;

    const normalizedTypeName = typeName.toLowerCase().replaceAll(" ", "");
    const userType = availableUserTypes.find(
      (type) =>
        type.title.toLowerCase().replaceAll(" ", "") === normalizedTypeName
    );

    console.log("Found user type:", userType);
    return userType?.id || null;
  };

  const findGroupId = (groupName) => {
    console.log("Finding group for:", groupName);
    console.log("Available groups:", availableGroups);

    if (!groupName || !availableGroups.length) return null;

    const group = availableGroups.find(
      (g) => g.name.toLowerCase() === groupName.toLowerCase()
    );

    console.log("Found group:", group);
    return group?.id || null;
  };

  // Transform CSV data to include IDs for backend
  const transformDataForBackend = (data) => {
    return data.map((user) => {
      const typeName = user["Type"] || user.type;
      const groupName = user["Group"] || user.group;
      const typeId = findUserTypeId(typeName);
      const groupId = findGroupId(groupName);

      console.log(`User: ${user["Full Name"] || user.fullName}`);
      console.log(`Type: ${typeName} -> ID: ${typeId}`);
      console.log(`Group: ${groupName} -> ID: ${groupId}`);

      return {
        fullName: user["Full Name"] || user.fullName,
        email: user["Email"] || user.email,
        typeId: typeId,
        groupId: groupId,
        // Keep original text for display purposes
        typeText: typeName,
        groupText: groupName,
      };
    });
  };

  // Function to create error CSV
  const createErrorCSV = (errorUsers) => {
    const csvContent = Papa.unparse(errorUsers, {
      header: true,
      columns: ["fullName", "email", "userTypeId", "groupIds", "error"],
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "failed_users.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const { mutate: uploadMutate, isPending: loadUpload } = useMutation({
    mutationFn: () => {
      console.log("Starting upload mutation...");
      const transformedData = transformDataForBackend(csvData);
      console.log("Transformed data:", transformedData);

      const usersForBackend = transformedData.map((user) => ({
        fullName: user.fullName,
        email: user.email,
        userTypeId: user.typeId,
        groupIds: user.groupId ? [user.groupId] : [],
      }));

      console.log("Users for backend:", usersForBackend);

      return uploadUsersByAdmin({
        users: usersForBackend,
      });
    },
    onSuccess: (data) => {
      console.log("Upload response:", data);
      setUploadResults(data);

      // Check if the response is an array (error format) or success format
      if (Array.isArray(data) && data.length) {
        // All users failed
        const errorCount = data.length;

        setToast({
          open: true,
          message: `Upload failed! ${errorCount} user${
            errorCount !== 1 ? "s" : ""
          } could not be added due to errors. Click to download error report.`,
          severity: "error",
        });

        // Auto-download error CSV
        createErrorCSV(data);
      } else {
        // Check if there are any errors in the response
        const hasErrors = data.errors && data.errors.length > 0;
        const successCount = data.successCount || 0;
        const errorCount = data.errors ? data.errors.length : 0;

        if (hasErrors) {
          // Mixed results - some succeeded, some failed
          setToast({
            open: true,
            message: `Partially completed! ${successCount} user${
              successCount !== 1 ? "s" : ""
            } added successfully, ${errorCount} failed. Click to download error report.`,
            severity: "warning",
          });

          // Auto-download error CSV
          createErrorCSV(data.errors);
        } else {
          // All succeeded
          setToast({
            open: true,
            message: `Successfully uploaded ${
              successCount || csvData.length
            } user${(successCount || csvData.length) !== 1 ? "s" : ""}!`,
            severity: "success",
          });
        }
      }

      // Refresh queries
      queryClient.refetchQueries("adminUsers");

      // Close modal after a short delay to show the success message
      setTimeout(() => {
        onClose();
        // Reset state
        setStep(1);
        setCsvData([]);
        setFile(null);
        setEditingRow(null);
        setUploadResults(null);
      }, 3000);
    },
    onError: (error) => {
      console.error("Error uploading users:", error);

      // Show error toast
      setToast({
        open: true,
        message: `Failed to upload users: ${error.message || "Unknown error"}`,
        severity: "error",
      });
    },
  });

  const handleDrop = (acceptedFiles) => {
    // Handle case when we're clearing the file (acceptedFiles is null)
    if (acceptedFiles === null) {
      setFile(null);
      setCsvData([]);
      return;
    }

    // Normal file upload handling
    if (acceptedFiles && acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);

      Papa.parse(uploadedFile, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (result) => {
          console.log("Raw parse result:", result);

          // Clean headers by trimming whitespace
          const cleanedData = result.data.map((row) => {
            const cleanedRow = {};
            Object.keys(row).forEach((key) => {
              const cleanKey = key.trim();
              const value = row[key];
              cleanedRow[cleanKey] =
                typeof value === "string" ? value.trim() : value;
            });
            return cleanedRow;
          });

          // Filter out completely empty rows
          const filteredData = cleanedData.filter((row) => {
            const fullName = row["Full Name"] || row.fullName || "";
            const email = row["Email"] || row.email || "";
            const type = row["Type"] || row.type || "";
            const group = row["Group"] || row.group || "";

            // Keep row if at least one field has content
            return fullName || email || type || group;
          });

          console.log("Filtered data:", filteredData);
          setCsvData(filteredData);
        },
        error: (error) => {
          console.error("CSV parsing error:", error);
          setToast({
            open: true,
            message: `CSV parsing error: ${error.message}`,
            severity: "error",
          });
        },
      });
    }
  };

  const handleEditRow = (index) => {
    setEditingRow(index);
  };

  const handleSaveRow = (index, field, value) => {
    const updatedData = [...csvData];
    updatedData[index][field] = value;
    setCsvData(updatedData);
    setEditingRow(null);
  };

  const handleSubmit = () => {
    console.log("Submit button clicked");
    console.log("CSV data:", csvData);
    console.log("Available user types:", availableUserTypes);
    console.log("Available groups:", availableGroups);

    // Validate that all users have valid type and group IDs
    const transformedData = transformDataForBackend(csvData);
    const invalidUsers = transformedData.filter((user) => !user.typeId);

    if (invalidUsers.length > 0) {
      console.error("Some users have invalid type:", invalidUsers);
      setToast({
        open: true,
        message: "Some users have invalid user types. Please check the data.",
        severity: "error",
      });
      return;
    }

    // Proceed with upload
    uploadMutate();
  };

  const handleNextStep = () => {
    setStep(step + 1);
  };

  const handleBackStep = () => {
    setStep(step - 1);
  };

  const handleDeleteRow = (index) => {
    const updatedData = [...csvData];
    setCsvData(updatedData.filter((_, i) => i !== index));
  };

  const handleRedirectToGroups = () => {
    onClose();
    // Dispatch custom event to redirect to Manage Groups tab
    window.dispatchEvent(new CustomEvent("redirectToGroupsTab"));
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleDownloadErrorReport = () => {
    if (uploadResults) {
      if (Array.isArray(uploadResults)) {
        createErrorCSV(uploadResults);
      } else if (uploadResults.errors && uploadResults.errors.length > 0) {
        createErrorCSV(uploadResults.errors);
      }
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <AddCSV
            handleBackStep={handleBackStep}
            handleNextStep={handleNextStep}
            csvData={csvData}
            handleDrop={handleDrop}
            file={file}
            onRedirectToGroups={handleRedirectToGroups}
            availableUserTypes={availableUserTypes}
            availableGroups={availableGroups}
          />
        );
      case 2:
        return (
          <UserList
            csvData={csvData}
            handleSaveRow={handleSaveRow}
            editingRow={editingRow}
            setEditingRow={setEditingRow}
            handleEditRow={handleEditRow}
            handleBackStep={handleBackStep}
            handleSubmit={handleSubmit}
            handleDeleteRow={handleDeleteRow}
            availableGroups={availableGroups}
            availableUserTypes={availableUserTypes}
            findUserTypeId={findUserTypeId}
            findGroupId={findGroupId}
            loadUpload={loadUpload}
          />
        );
      default:
        return null;
    }
  };

  // Reset state when modal is closed
  const handleClose = () => {
    setStep(1);
    setCsvData([]);
    setFile(null);
    setEditingRow(null);
    setUploadResults(null);
    setToast({ open: false, message: "", severity: "success" });
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <div
          className="modal-content"
          style={{
            padding: "20px",
            backgroundColor: "white",
            margin: "1% auto",
            width: "600px",
            minWidth: "400px",
            transform: "translate(-50%, -50%)",
            maxHeight: "90vh",
            overflowY: "auto",
            position: "absolute",
            top: "50%",
            left: "50%",
          }}
        >
          <div style={headerStyle}>
            <Typography
              variant="h6"
              component="h2"
              style={{ fontWeight: "bold" }}
            >
              Add users
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>

          <div style={contentStyle}>{renderStepContent()}</div>
        </div>
      </Modal>

      {/* Success/Error Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.severity === "success" ? 4000 : 8000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        action={
          (toast.severity === "error" || toast.severity === "warning") &&
          uploadResults &&
          (Array.isArray(uploadResults) ||
            (uploadResults.errors && uploadResults.errors.length > 0)) ? (
            <IconButton
              size="small"
              color="inherit"
              onClick={handleDownloadErrorReport}
            >
              <Download />
            </IconButton>
          ) : null
        }
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UploadUsersModal;

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
};

const contentStyle = {
  width: "100%",
  minHeight: "300px",
};
